const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

// Desktop-only viewer — constant keeps the server whitelist + dashboard
// device split working unchanged.
type ViewMode = 'desktop' | 'mobile';
const VIEW_MODE: ViewMode = 'desktop';

export type EventType = 'card_expanded' | 'slider_settled' | 'cta_clicked' | 'rerun_clicked' | 'section_time';

export interface TrackPayload {
  cardId?: string;
  category?: string;
  pct?: number;
  url?: string;
  /** section_time: which deck section, and how long (ms) the client spent in it. */
  section?: string;
  ms?: number;
}

interface TrackedEvent {
  type: EventType;
  payload?: TrackPayload;
  ts?: number;
  viewMode?: ViewMode;
}

/**
 * Fire-and-forget engagement event from the report viewer. Uses
 * navigator.sendBeacon when available so events still flush during page
 * unload (e.g. user clicks the booking CTA and immediately navigates away).
 * Falls back to fetch-with-keepalive in browsers without sendBeacon.
 *
 * Errors are swallowed — analytics must never break the user-facing flow.
 *
 * Wire format wraps a single event in an `events: [...]` array so we can
 * batch later without changing the contract.
 */
export function trackEvent(slug: string, type: EventType, payload?: TrackPayload): void {
  if (!slug || !type) return;
  if (!API_URL) return; // dev with unset VITE_API_URL — no-op rather than 404 spam

  const url = `${API_URL}/api/public/web-reports/${slug}/events`;
  const body: { events: TrackedEvent[] } = {
    events: [{ type, payload, ts: Date.now(), viewMode: VIEW_MODE }],
  };
  const json = JSON.stringify(body);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([json], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }

    // Fallback: keepalive flag lets the request finish even if the page
    // is being torn down.
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
    }).catch(() => {
      /* swallow */
    });
  } catch {
    /* swallow — analytics never breaks the flow */
  }
}
